import React, { useCallback, useEffect, useState } from 'react';
import {
  MailOutlined,
  ReloadOutlined,
  LoadingOutlined,
  KeyOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { emailAPI } from '@/api/endpoints/email.js';
import styles from '@/styles/pages/email/EmailPage.module.css';

const REFRESH_INTERVAL_MS = 15000;
const PAGE_SIZE = 20;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Только что';
  if (diffMins < 60) return `${diffMins} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const EmailPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = 'Почта | DevSpace';
  }, []);

  const loadEmails = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    const response = await emailAPI.getEmails(200);
    if (response.success) {
      setEmails(response.data || []);
      setError(null);
    } else {
      setError(response.error || 'Не удалось загрузить письма');
    }
    if (showSpinner) setLoading(false);
  }, []);

  useEffect(() => {
    loadEmails(true);
    const interval = setInterval(() => loadEmails(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadEmails]);

  const totalPages = Math.max(1, Math.ceil(emails.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageEmails = emails.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingOutlined className={styles.loadingSpinner} />
        <p>Загрузка писем...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <MailOutlined className={styles.titleIcon} />
            Почта
          </h1>
          <button className={styles.refreshButton} onClick={() => loadEmails(true)}>
            <ReloadOutlined /> Обновить
          </button>
        </div>

        {error && (
          <div className={styles.errorMessage}>{error}</div>
        )}

        <div className={styles.emailsContainer}>
          <div className={styles.emailsList}>
            {pageEmails.map((email) => (
              <div key={email.id} className={styles.emailCard}>
                <div className={styles.emailHeader}>
                  <div className={styles.emailAddr}>
                    <MailOutlined /> {email.addr}
                  </div>
                  <div className={styles.emailTime}>
                    <ClockCircleOutlined /> {formatDate(email.received_at)}
                  </div>
                </div>

                <div className={styles.emailMeta}>
                  <span className={styles.emailFrom}>
                    <UserOutlined /> {email.from_addr || 'неизвестно'}
                  </span>
                  {email.code && (
                    <span className={styles.emailCode}>
                      <KeyOutlined /> {email.code}
                    </span>
                  )}
                </div>

                {email.subject && (
                  <div className={styles.emailSubject}>{email.subject}</div>
                )}

                {email.body_preview && (
                  <div className={styles.emailPreview}>{email.body_preview}</div>
                )}
              </div>
            ))}

            {emails.length === 0 && !loading && (
              <div className={styles.emptyState}>
                <MailOutlined className={styles.emptyIcon} />
                <h3>Писем пока нет</h3>
                <p>Письма, пойманные вебхуком Cloudflare, появятся здесь</p>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.pageButton} disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                ← Назад
              </button>
              <span>{currentPage} / {totalPages}</span>
              <button className={styles.pageButton} disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
                Вперёд →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailPage;
